/**
 * OneAmerica Category 3 demographic Single Pay calibration bands.
 *
 * Historical datasets: cf0f349439f7c447e289+7e3bff923ad089e34b23
 * Curves: 1152
 * Override rows: 3351
 * Override SHA-256: 70847c05238ae51ca27bcb60fccae45e1fd8cf39431bb0c7d3e79520888643b9
 *
 * Category 3 is collected independently from Category 1. The historical $100k+
 * multipliers were verified equal, so only fresh lower-premium points are
 * stored here as overrides instead of duplicating all 7,162 historical tuples.
 */

import type { PackedBandEntry } from './oneAmericaPricingUtils';
import { OA_CAT1_DEMOGRAPHIC_BANDS } from './oneAmericaCat1DemographicBands';
import rawOverrides from './oneAmericaCat3DemographicSinglePayBands.data.json?raw';

export const OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_AGES = [40, 45, 50, 55, 60, 65, 70, 75] as const;

export const OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_OVERRIDES = JSON.parse(rawOverrides) as Record<
  string,
  PackedBandEntry[]
>;

const INDEPENDENTLY_COLLECTED_PREMIUMS = new Set([50000, 75000, 125000]);

function isCategory3Key(key: string): boolean {
  const [, , , , productKey, aobInflation, cobInflation] = key.split('|');
  return productKey === 'sp_single' && aobInflation !== '2%' && cobInflation !== '2%';
}

function withOverrides(
  historical: readonly PackedBandEntry[],
  fresh: readonly PackedBandEntry[] = [],
): PackedBandEntry[] {
  const byPremium = new Map<number, PackedBandEntry>();
  for (const entry of historical) {
    // Category 1 now has independently scraped points at these premiums too.
    // Do not let them leak into Category 3 when its own provider response is a
    // boundary rejection rather than a usable quote.
    if (!INDEPENDENTLY_COLLECTED_PREMIUMS.has(entry[0])) byPremium.set(entry[0], entry);
  }
  for (const entry of fresh) byPremium.set(entry[0], entry);
  return [...byPremium.values()].sort((a, b) => a[0] - b[0]);
}

export const OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_BANDS = Object.fromEntries(
  Object.entries(OA_CAT1_DEMOGRAPHIC_BANDS)
    .filter(([key]) => isCategory3Key(key))
    .map(([key, historical]) => [
      key,
      withOverrides(historical, OA_CAT3_DEMOGRAPHIC_SINGLE_PAY_OVERRIDES[key]),
    ]),
) as Record<string, PackedBandEntry[]>;
